/**
 * `/` 랜딩 — §14 "로그인/가입(§10.0 승인제)·방 만들기/코드 입장".
 *
 * §10.0 인증 계층을 그대로 화면에 옮긴다:
 *  - 가입은 즉시 로그인이 아니라 **관리자 승인 대기(pending)** 로 끝난다.
 *  - 방 개설은 승인 계정만(익명 개설 금지 — GPU 자원 보호).
 *  - **뷰어는 6자리 패스코드만으로 입장한다**(§10.0 정정) — 계정 불요.
 *    통제가 걸리는 곳은 개설(호스트)이다.
 *  - 공개 입장(public_entry)은 방별 opt-in — 켠 방만 **패스코드 없이**
 *    직통 링크(#k=)로 입장 가능하다.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { SectionCard } from '@cupia/ui/section-card'
import { Field } from '@cupia/ui/field'
import { Input } from '@cupia/ui/input'
import { Checkbox } from '@cupia/ui/checkbox'
import { Button } from '@cupia/ui/button'
import { Badge } from '@cupia/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@cupia/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cupia/ui/select'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@cupia/ui/input-otp'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@cupia/ui/tabs'
import { Separator } from '@cupia/ui/separator'
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  LogOut,
  Radio,
  ShieldCheck,
} from 'lucide-react'
import {
  ApiError,
  createRoom,
  joinByPasscode,
  listMyMeetings,
  login,
  logout,
  me,
  signup,
  type Account,
  type MeetingSummary,
  type ServerLanguage,
} from '../lib/api'
import { saveCredential } from '../lib/session'
import { saveHostCredential } from '../lib/host-credential'
import { USE_MOCK } from '../lib/config'
import { Brand } from '../components/brand'
import { AdminLink } from '../components/admin-link'
import { MyRoomsPanel } from '../components/my-rooms-panel'
import { RoomHistoryPanel } from '../components/room-history-panel'
import { ThemeControls } from '../components/theme-controls'
import { SwitchRow } from '../components/switch-row'
import { LanguageSelect } from '../components/language-select'
import { LANDING_TARGETS, SOURCE_LANGUAGES, TARGET_LANGUAGES, type Language } from '../lib/languages'
import { useServerConfig } from '../lib/use-server-config'

/**
 * 개설 화면에 보이는 **원어 선택지**(STAGE1 I2 §5).
 *
 * 숨김 원어(`TRANS_HIDDEN_SOURCE_LANGS`)를 거르는 곳은 **서버 한 곳**이다 —
 * `/api/config` 의 `languages` 가 이미 뺀 목록이라 화면은 그것으로 거르기만
 * 한다. 필터를 화면에도 두면 두 벌이 되고, 한쪽만 갱신되면 화면에만 있는
 * 언어를 골라 개설 API 에서 400 을 맞는다(이 미러 파일이 생긴 그 사고).
 *
 * 미러를 버리지는 않는다: `/api/config` 는 `script` 를 안 보내고, 구버전
 * 서버는 `languages` 자체를 안 보낸다. 그래서 **서버 목록으로 거르고 표시는
 * 미러 항목**을 쓰며, 서버가 말이 없으면 미러 전체로 되돌아간다 — 빈 목록은
 * 아무 언어도 못 고르게 만들어 숨김이 안 걸리는 것보다 나쁘다.
 *
 * 표시 순서는 미러를 따른다(서버 응답 순서가 화면을 흔들지 않는다).
 */
export function visibleSourceOptions(
  allowed: readonly ServerLanguage[] | undefined,
): readonly Language[] {
  if (!allowed) return SOURCE_LANGUAGES
  const codes = new Set(allowed.filter((l) => l.source).map((l) => l.code))
  return SOURCE_LANGUAGES.filter((l) => codes.has(l.code))
}

/**
 * 개설 화면의 **기본 원어**(STAGE1 I2FIX — codex 리뷰가 잡은 400).
 *
 * `src` 의 초깃값은 `visibleSourceOptions` 가 서버 응답으로 채워지기 전에
 * 고정 리터럴('vi')로 잡힌다. 그 언어가 숨김(`TRANS_HIDDEN_SOURCE_LANGS`)
 * 이면 선택지에는 없는데 상태는 그대로 남아 제출 시 개설 API 가 400 을
 * 낸다(`landing.tsx:279/345` 서버가 아는 언어인지 검증).
 *
 * 규칙: **기존 기본값이 보이면 그대로 둔다**(동작을 안 바꾼다). 숨겨진
 * 경우에만 첫 가시 언어로 떨어진다. 선택지가 아직 비어 있으면(서버 응답
 * 전 첫 렌더) 기존 값을 유지한다 — 빈 값으로 내렸다가 응답이 오면 다시
 * 바뀌는 깜빡임을 피한다.
 */
export function resolveDefaultSource(
  current: string,
  options: readonly Language[],
): string {
  if (options.length === 0) return current
  if (options.some((l) => l.code === current)) return current
  return options[0]!.code
}

export function LandingRoute(): React.ReactElement {
  const [account, setAccount] = useState<Account | null>(null)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    me()
      .then(setAccount)
      .catch(() => setAccount(null))
      .finally(() => setChecking(false))
  }, [])

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <header className="border-b border-[var(--color-border)]">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <Brand subtitle="실시간 다국어 통역 중계" />
          <div className="ms-auto flex items-center gap-2">
            {account && (
              <>
                <Badge variant="softSuccess" className="gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  {account.username}
                </Badge>
                {/* §10.0 관리자에게만 보이는 진입점(통제는 서버가 한다) */}
                <AdminLink isAdmin={account.is_admin} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void logout().then(() => setAccount(null))
                  }}
                >
                  <LogOut className="h-3.5 w-3.5" />
                  로그아웃
                </Button>
              </>
            )}
            <ThemeControls />
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-5xl gap-6 px-6 py-8 md:grid-cols-2">
        {checking ? (
          <div className="md:col-span-2 text-center text-[var(--color-muted-foreground)]">
            계정 상태 확인 중…
          </div>
        ) : account ? (
          <CreateRoomPanel account={account} />
        ) : (
          <AuthPanel onAuthenticated={setAccount} />
        )}

        <JoinPanel account={account} />

        {/* §9.2 — 내가 연 회의실. 브라우저를 닫아도 방은 재접속을 위해
            잠시 열려 있다(§9). 자기가 만든 것은 자기가 치울 수 있어야 한다. */}
        {account && <MyRoomsPanel />}
        {account && <RoomHistoryPanel />}
      </main>
    </div>
  )
}

/* ── §10.0 로그인 / 가입 ─────────────────────────────────────────────────── */

function AuthPanel({
  onAuthenticated,
}: {
  onAuthenticated: (account: Account) => void
}): React.ReactElement {
  const [tab, setTab] = useState('login')
  const [id, setId] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  const [signedUp, setSignedUp] = useState(false)

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setBusy(true)
      setError(null)
      setPending(false)
      try {
        if (tab === 'login') {
          onAuthenticated(await login(id, password))
        } else {
          await signup(id, password)
          setSignedUp(true)
        }
      } catch (err) {
        if (err instanceof ApiError && err.code === 'pending_approval') {
          // §10.0 — 승인 전에는 로그인되지 않는다.
          setPending(true)
        } else {
          setError(err instanceof ApiError ? err.message : '요청이 실패했습니다.')
        }
      } finally {
        setBusy(false)
      }
    },
    [tab, id, password, onAuthenticated],
  )

  return (
    <SectionCard title="계정" className="h-fit">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 w-full">
          <TabsTrigger value="login" className="flex-1">
            로그인
          </TabsTrigger>
          <TabsTrigger value="signup" className="flex-1">
            가입 신청
          </TabsTrigger>
        </TabsList>

        <TabsContent value="login" className="mt-0">
          <p className="mb-3 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
            방 개설과 비공개 방 입장에는 관리자 승인을 받은 계정이 필요합니다.
          </p>
        </TabsContent>
        <TabsContent value="signup" className="mt-0">
          <p className="mb-3 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
            가입 신청 후 관리자 승인이 완료되어야 로그인할 수 있습니다.
          </p>
        </TabsContent>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <Field label="아이디" orientation="stacked" required>
            <Input
              value={id}
              onChange={(e) => setId(e.target.value)}
              autoComplete="username"
              placeholder="user"
              required
            />
          </Field>
          <Field
            label="비밀번호"
            orientation="stacked"
            required
            help={tab === 'signup' ? '8자 이상' : undefined}
          >
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
              required
            />
          </Field>

          {pending && (
            <Alert variant="warning">
              <Clock className="h-4 w-4" />
              <AlertTitle>승인 대기 중</AlertTitle>
              <AlertDescription>
                관리자 승인이 완료되면 로그인할 수 있습니다.
              </AlertDescription>
            </Alert>
          )}

          {signedUp && (
            <Alert variant="success">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>가입 신청이 접수되었습니다</AlertTitle>
              <AlertDescription>
                관리자 승인 후 로그인할 수 있습니다. 승인 전에는 방을 만들 수 없습니다.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* 주 동선 버튼은 DS 의 lg 크기를 쓴다 — 기본 sm 은
              32px 라 휴대폰에서 손가락 탭 영역(≥44px)에 못 미친다.
              직접 h-* 를 박지 않는다(DS 토큰과 어긋난다). */}
          <Button type="submit" size="lg" disabled={busy}>
            {busy ? '처리 중…' : tab === 'login' ? '로그인' : '가입 신청'}
          </Button>
        </form>
      </Tabs>
    </SectionCard>
  )
}

/* ── 방 만들기(승인 계정 전용) ──────────────────────────────────────────── */

function CreateRoomPanel({ account }: { account: Account }): React.ReactElement {
  const navigate = useNavigate()
  const [src, setSrc] = useState('vi')
  const [dst, setDst] = useState('ko')
  // §bidi 양방향 — 개설 화면에는 **스위치가 없다**(2026-09-07 사용자 결정: 비활성
  // 스위치가 혼란만 줬다). 양방향은 회의 중 host 콘솔의 방향 전환(fr↔ko 등
  // 비대칭 양방향, bidi_asym)으로만 켠다. 서버 계약(`bidirectional` 필드,
  // tests/test_inventory.py)은 유지하므로 항상 false 로 실어 보낸다.
  const bidirectional = false
  const [publicEntry, setPublicEntry] = useState(false)
  const serverConfig = useServerConfig()
  // 원어 선택지는 **서버가 거른 목록**을 쓴다(STAGE1 I2 §5, codex I1 검토 #3).
  // 숨김 원어(`TRANS_HIDDEN_SOURCE_LANGS`)는 `/api/config` 가 이미 빼고 내려
  // 준다 — 필터를 화면에도 두면 두 벌이 되고, 그러면 한쪽만 갱신돼 화면에만
  // 있는 언어를 골라 400 을 맞는 그 사고가 난다(생성 미러가 생긴 이유).
  //
  // 미러를 아예 버리지는 않는다: `/api/config` 는 `script` 를 안 보내고
  // (`as_payload()`), 구버전 서버는 `languages` 자체를 안 보낸다. 그래서
  // **서버 목록으로 거르고 표시는 미러 항목**을 쓴다 — 서버가 말 안 하면
  // 미러 전체로 되돌아간다(빈 목록보다 낫다).
  const sourceOptions = useMemo(
    () => visibleSourceOptions(serverConfig.languages),
    [serverConfig.languages],
  )
  // 기본 원어가 숨김이면 첫 가시 언어로 되돌린다(위 `resolveDefaultSource`
  // 참조) — 그 외에는 선택을 건드리지 않는다.
  useEffect(() => {
    setSrc((cur) => resolveDefaultSource(cur, sourceOptions))
  }, [sourceOptions])
  // 2026-09-22 사용자 결정 — 자막 기록은 **기본 켜짐**. opt-in 이면 실사용에서
  // 아무도 안 켜고, 긴 회의(>TRANS_RING_MAX)·재기동 뒤에는 링 잔여분만 남아
  // 회의록이 통째로 복구 불가가 된다. 서버 모델 기본(`CreateRoomBody.record`)
  // 도 True 라 이제 양쪽이 같다.
  const [record, setRecord] = useState(true)
  // S28 — 회의 이름·주제(둘 다 opt-in). 서버가 기본값을 지어내지 않으므로
  // 화면도 빈 채로 항상 전송한다.
  const [name, setName] = useState('')
  const [topic, setTopic] = useState('')
  // S28 — 호스트의 음성 저장(원본 오디오) opt-out. `record`(자막·대화 텍스트
  // 기록)와는 **다른 축**이다 — 혼동하지 않는다. 실사용에서 opt-in 요구는
  // 아무도 안 켠다는 것이 결론이라 기본 ON.
  const [recordAudio, setRecordAudio] = useState(true)
  // §7.2 방별 코치. 서버가 코치를 끈 배포에서는 스위치를 그리지 않는다 —
  // 켤 수 없는 스위치를 보여 주면 켰다고 믿게 된다.
  const [coach, setCoach] = useState(false)
  const [coachPublic, setCoachPublic] = useState(true)
  const [assistPublic, setAssistPublic] = useState(false)
  const [retention, setRetention] = useState<'7' | '30' | '90'>('30')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // §S23 이전 회의 이어하기 — 목록 로드 실패는 셀렉터만 숨긴다(개설을 막지
  // 않는다). 빈 값 = 이어하지 않음.
  const [continueFrom, setContinueFrom] = useState('')
  const [prevMeetings, setPrevMeetings] = useState<MeetingSummary[]>([])
  const [prevMeetingsAvailable, setPrevMeetingsAvailable] = useState(true)

  useEffect(() => {
    listMyMeetings()
      .then((res) => setPrevMeetings(res.meetings))
      .catch((err) => {
        setPrevMeetingsAvailable(false)
        console.error('이전 회의 목록을 불러오지 못했습니다.', err)
      })
  }, [])

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setBusy(true)
      setError(null)
      try {
        const created = await createRoom({
          src,
          dst,
          bidirectional,
          public_entry: publicEntry,
          record,
          retention_days: Number(retention) as 7 | 30 | 90,
          coach,
          coach_public: coachPublic,
          assist_public: assistPublic,
          continue_from: continueFrom,
          name,
          topic,
          record_audio: recordAudio,
        })
        // 재입장(내가 연 회의실)과 같은 저장 함수 — 두 벌 금지(DEF-131).
        // src/dst 는 개설 폼 값을 그대로 싣는다.
        saveHostCredential({ ...created, src, dst })
        void navigate('/host')
      } catch (err) {
        setError(err instanceof ApiError ? err.message : '방을 만들지 못했습니다.')
      } finally {
        setBusy(false)
      }
    },
    [
      src,
      dst,
      bidirectional,
      publicEntry,
      record,
      coach,
      coachPublic,
      assistPublic,
      retention,
      continueFrom,
      name,
      topic,
      recordAudio,
      navigate,
    ],
  )

  return (
    <SectionCard title="방 만들기" className="h-fit">
      <form onSubmit={submit} className="flex flex-col gap-3">
        <p className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          {account.username} 계정으로 개설합니다. 개설 시 6자리 입장 코드와 호스트 키가 발급됩니다.
        </p>

        {prevMeetingsAvailable && prevMeetings.length > 0 && (
          <Field label="이전 회의 이어하기" orientation="stacked">
            <Select value={continueFrom} onValueChange={setContinueFrom}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">이어하지 않음</SelectItem>
                {prevMeetings.map((m) => (
                  <SelectItem key={m.id} value={m.room}>
                    {m.room} · {new Date(m.started_at * 1000).toLocaleString('ko-KR')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        )}

        <Field label="회의실 이름" orientation="stacked">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            maxLength={60}
            placeholder="예: 8월 관세 협상"
          />
        </Field>

        <Field label="주제" orientation="stacked">
          <Input
            value={topic}
            onChange={(e) => setTopic(e.target.value.slice(0, 200))}
            maxLength={200}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          {/* S38-2 R1-d — 라벨·문구 변경만. 실회의(fr 방)에서 영어 57건·
              한국어 42건이 정상 처리됐다 — "원언어" 라는 이름이 그 언어만
              듣는다는 오해를 준다. "자동" 선택지는 넣지 않는다: 언어
              선언을 빼면 프롬프트가 깨지거나(src="") 토큰이 새고
              (src="auto") 붕괴율이 20%→58% 로 오른다(omni.py 실측). */}
          <Field
            label="주로 쓰는 언어"
            orientation="stacked"
            help="다른 언어로 말해도 인식·통역됩니다."
          >
            <LanguageSelect
              options={sourceOptions}
              value={src}
              onChange={setSrc}
              label="주로 쓰는 언어"
            />
          </Field>
          <Field label="타깃 언어" orientation="stacked">
            {/* S38-1 — 타깃은 **한국어만** 노출한다.
                서버 카탈로그는 {ko, vi} 를 타깃으로 인정하지만(양방향이 그
                자격에 의존한다 — languages.py `is_bidirectional_pair`),
                화면에서 vi 를 고르게 두면 "프랑스어 → 베트남어" 처럼 이
                제품이 쓰지 않는 조합이 만들어진다. 서버 자격은 건드리지
                않는다 — 빼는 순간 양방향이 개설 불가가 된다(실측 확인). */}
            <LanguageSelect
              options={TARGET_LANGUAGES.filter((l) => (LANDING_TARGETS as readonly string[]).includes(l.code))}
              value={dst}
              onChange={setDst}
              label="타깃 언어"
            />
          </Field>
        </div>



        <Separator className="my-1" />

        <SwitchRow
          label="공개 입장 허용"
          description="켜면 패스코드 없이 직통 링크(#k=)로 입장할 수 있습니다 (읽기 전용)."
          checked={publicEntry}
          onCheckedChange={setPublicEntry}
        />

        {/* 2026-09-22 — 기본 켜짐. 문구도 opt-in 전제("무저장 기본")에서
            정정한다: 끄면 회의록이 메모리 링 범위(최근 발화)로 줄어든다. */}
        <SwitchRow
          label="자막 기록"
          description="기본 켜짐. 끄면 방 종료와 함께 자막이 소멸하고, 회의록은 최근 발화(메모리 링) 범위만 남습니다."
          checked={record}
          onCheckedChange={setRecord}
        />

        {/* 조용한 미기록 금지 — 서버가 기록 디렉터리를 안 가졌으면 스위치를
            켜도 디스크에 안 남는다. 그 사실을 여기서 말한다. */}
        {record && !serverConfig.record_persisted && (
          <p className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
            디스크 기록 꺼짐(서버 설정) — 회의록은 최근 발화(메모리 링) 범위만
            저장됩니다.
          </p>
        )}

        {/* S28 — 호스트의 음성 저장(원본 오디오) opt-out. 기본 ON. */}
        <label className="flex items-center gap-2 text-(length:--text-sm-size)">
          <Checkbox
            checked={recordAudio}
            onCheckedChange={(v) => setRecordAudio(v === true)}
          />
          음성 저장
        </label>

        {serverConfig.coach && (
          <SwitchRow
            label="통역 코치"
            description="회의 중 진행 도움말(요지·제안·미답)을 띄웁니다."
            checked={coach}
            onCheckedChange={setCoach}
          />
        )}

        {/* 코치를 켠 방에서만 의미가 있다 — 끈 방에 이 스위치를 두면
            "켰는데 아무것도 안 나온다" 가 된다. */}
        {serverConfig.coach && coach && (
          <SwitchRow
            label="코치를 참가자에게도 공개"
            description="끄면 호스트만 봅니다. 상대측과 화면을 함께 보는 자리에서는 끄는 편이 좋습니다."
            checked={coachPublic}
            onCheckedChange={setCoachPublic}
          />
        )}

        {/* 어시스트 질문·답을 참가자에게도 보일지(D1). 기본 off —
            질문 본문이 곧 개인정보일 수 있어 기본 공개는 되돌릴 수 없다. */}
        {serverConfig.assist && (
          <SwitchRow
            label="참가자 질문 공개"
            description="끄면 질문한 참가자와 호스트만 답을 봅니다."
            checked={assistPublic}
            onCheckedChange={setAssistPublic}
          />
        )}

        {record && (
          <Field label="보존 기간" orientation="stacked">
            <Select value={retention} onValueChange={(v) => setRetention(v as '7' | '30' | '90')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7일</SelectItem>
                <SelectItem value="30">30일</SelectItem>
                <SelectItem value="90">90일</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" size="lg" disabled={busy}>
          <Radio className="h-4 w-4" />
          {busy ? '개설 중…' : '방 만들고 시작'}
        </Button>
      </form>
    </SectionCard>
  )
}

/* ── 코드 입장(2요소) ───────────────────────────────────────────────────── */

function JoinPanel({ account }: { account: Account | null }): React.ReactElement {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (code.length !== 6) return
      setBusy(true)
      setError(null)
      try {
        // 보내는 것은 패스코드 하나 — 방 ID 는 서버가 알려준다(§10.0).
        // 로그인 상태면 계정 토큰을 함께 실어 채팅 주체의 계정 축을
        // 유지한다(§5.5 — stream 은 세션을 못 본다).
        const result = await joinByPasscode(code, { authenticated: !!account })
        saveCredential({
          room: result.room,
          // 서버는 더는 view_key 를 싣지 않는다 — 자격은 토큰뿐.
          key: result.view_key ?? '',
          role: 'viewer',
          // §5.2 viewer 토큰 + 재교환용 패스코드: 만료 갱신은 이 패스코드로
          // join 을 다시 지나는 것이다(stream 소유 — auth 다운에도 갱신됨).
          ...(result.token
            ? {
                token: result.token,
                tokenExp: Date.now() + (result.token_ttl_s ?? 600) * 1000,
              }
            : {}),
          passcode: code,
          src: result.src,
          dst: result.dst,
          name: result.name,
          topic: result.topic,
        })
        void navigate(`/v/${encodeURIComponent(result.room)}`)
      } catch (err) {
        // §10.2 존재 미누출 — 서버가 uniform 하게 응답하므로 화면도 구분하지 않는다.
        setError(err instanceof ApiError ? err.message : '입장할 수 없습니다.')
      } finally {
        setBusy(false)
      }
    },
    [code, account, navigate],
  )

  return (
    <SectionCard title="코드로 입장" className="h-fit">
      <form onSubmit={submit} className="flex flex-col items-center gap-4">
        <p className="text-center text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
          회의실에서 공유된 6자리 입장 패스코드를 입력하세요. 계정 없이 입장할 수 있습니다.
        </p>

        <InputOTP maxLength={6} value={code} onChange={setCode}>
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        {!account && (
          <Alert variant="info">
            <ShieldCheck className="h-4 w-4" />
            <AlertTitle>계정 없이 입장할 수 있습니다</AlertTitle>
            <AlertDescription>
              참가자는 6자리 패스코드만 있으면 됩니다. 계정은 방을 여는 호스트에게만
              필요합니다.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={busy || code.length !== 6}>
          {busy ? '확인 중…' : '입장'}
        </Button>

        {/* 목 모드 전용 힌트 — 실 번들에 데모 코드가 실려 나가면 안 된다. */}
        {USE_MOCK && (
          <p className="text-center text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
            데모 방 코드: <span className="font-mono">482100</span>
          </p>
        )}
      </form>
    </SectionCard>
  )
}
